import { Helmet } from "react-helmet-async";
import type { Lang } from "@/utils/i18n";
import { useLanguage } from "@/components/providers/language-provider";
import { termsIt } from "./legal/terms.it";

interface Section {
  title: string;
  body: string[];
}

interface Content {
  title: string;
  updated: string;
  metaDescription: string;
  sections: Section[];
}

// Ukrainian (uk) has no legal text yet and falls back to English below.
const content: Partial<Record<Lang, Content>> & { en: Content } = {
  en: {
    title: "Terms of Use",
    updated: "Last updated: October 23, 2026",
    metaDescription: "MoovaTV Terms of Use. Read our terms and conditions for using the service.",
    sections: [
      {
        title: "1. Acceptance of Terms",
        body: [
          "By accessing or using MoovaTV (the \"Service\"), including our applications for Smart TVs (Samsung, LG) and streaming devices (Roku, Amazon Fire TV), mobile apps (Android and iOS), and the website moova.tv, you agree to be bound by these Terms of Use. If you do not agree with any part of these terms, you must not use the Service.",
          "These terms constitute a legally binding agreement between you (\"User\") and MoovaTV (\"we\", \"us\", or \"Company\"). We recommend reading them carefully before using the Service.",
        ],
      },
      {
        title: "2. Service Description",
        body: [
          "MoovaTV is exclusively a media player application. We do NOT sell, distribute, host, or provide playlists, channels, streams, or content of any kind. The Service functions solely as a player that allows users to organize and play their own media content from external sources.",
          "Users are entirely responsible for the content they add to the application, including playlists, playlist credentials, and any other media sources. MoovaTV has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third-party sources.",
          "The Service is provided \"as is\" and \"as available\" without warranties of any kind, either express or implied.",
        ],
      },
      {
        title: "3. Account & Activation",
        body: [
          "Device activation on MoovaTV is done through the device's unique MAC address. Each license is tied to a specific device and is non-transferable unless explicitly allowed by our support team.",
          "We offer the following plans: Free Trial (7 days), Annual Plan (US$ 6/year), and Lifetime Plan (US$ 12 one-time payment). Prices may be adjusted at any time, but changes will not affect active plans that have already been purchased.",
          "Payments are processed securely through Gates2B / Stripe. We do not store complete credit card information on our servers. Account creation may also occur through authorized resellers.",
          "You are responsible for maintaining the security of your account credentials and for all activities that occur under your account.",
        ],
      },
      {
        title: "4. User Responsibilities",
        body: [
          "You are solely responsible for all content you add, access, or stream through the Service. You must ensure that your use of MoovaTV complies with all applicable local, state, national, and international laws and regulations.",
          "You agree NOT to use the Service to: access, distribute, or stream content that infringes on intellectual property rights of third parties; engage in any illegal activity; attempt to reverse-engineer, decompile, or disassemble the application; interfere with or disrupt the Service or servers; share your account credentials with unauthorized third parties; use the Service for commercial redistribution without authorization.",
          "Violation of these responsibilities may result in immediate termination of your account without refund.",
        ],
      },
      {
        title: "5. Intellectual Property",
        body: [
          "The MoovaTV brand, logo, source code, user interface design, and all related visual elements are the exclusive property of MoovaTV and are protected by applicable intellectual property laws.",
          "You are granted a limited, non-exclusive, non-transferable, revocable license to use the Service for personal, non-commercial purposes in accordance with these Terms.",
          "Content added by users to the Service remains the sole responsibility of the respective users. MoovaTV does not claim ownership of any user-provided content.",
        ],
      },
      {
        title: "6. Payment & Refunds",
        body: [
          "All payments are processed through Gates2B / Stripe using industry-standard encryption and security measures. Accepted payment methods include credit cards and other methods available through Gates2B / Stripe.",
          "We offer a 7-day money-back guarantee from the date of purchase. To request a refund within this period, contact us at support@moova.tv with your purchase details. Refunds are processed through the original payment method.",
          "After the 7-day period, payments are non-refundable. The Lifetime Plan provides access for the lifetime of the MoovaTV product, meaning for as long as the Service continues to operate. It does not guarantee perpetual availability of the Service.",
          "Promotional prices and discounts are subject to specific conditions and may be revoked at any time.",
        ],
      },
      {
        title: "7. Limitation of Liability",
        body: [
          "To the maximum extent permitted by applicable law, MoovaTV shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from or related to your use of the Service.",
          "We are not responsible for: any content accessed or streamed through the Service by users; the availability, accuracy, or legality of third-party content or services; service interruptions, whether due to maintenance, technical issues, or circumstances beyond our control; data loss resulting from device malfunction or user error.",
          "Our total liability for any claims arising from the use of the Service shall not exceed the amount paid by you for the Service in the 12 months preceding the claim.",
        ],
      },
      {
        title: "8. Privacy",
        body: [
          "Your privacy is important to us. Our collection, use, and protection of your personal information is governed by our Privacy Policy, available at moova.tv/privacy.",
          "By using the Service, you consent to the data practices described in our Privacy Policy. We encourage you to review it periodically for any updates.",
        ],
      },
      {
        title: "9. Modifications to Terms",
        body: [
          "We reserve the right to modify these Terms of Use at any time. Changes will be posted on this page with an updated revision date. We may notify you of significant changes through the application or via email.",
          "Your continued use of the Service after any modifications constitutes your acceptance of the updated terms. If you do not agree with the changes, you should discontinue use of the Service.",
        ],
      },
      {
        title: "10. Termination",
        body: [
          "We reserve the right to suspend or terminate your account and access to the Service at any time, with or without notice, for conduct that we believe violates these Terms of Use, is harmful to other users, or is otherwise objectionable.",
          "Upon termination, your right to use the Service ceases immediately. Provisions of these Terms that by their nature should survive termination shall remain in effect, including but not limited to ownership provisions, warranty disclaimers, and limitations of liability.",
          "You may terminate your account at any time by contacting us at support@moova.tv.",
        ],
      },
      {
        title: "11. Governing Law",
        body: [
          "These Terms of Use shall be governed by and construed in accordance with the laws of the Federative Republic of Brazil, without regard to conflict of law provisions.",
          "Any disputes arising from these Terms or the use of the Service shall be submitted to the competent courts of Brazil. Before initiating legal proceedings, the parties agree to attempt to resolve disputes amicably.",
        ],
      },
      {
        title: "12. Contact",
        body: [
          "If you have any questions, concerns, or requests regarding these Terms of Use, please contact us:",
        ],
      },
    ],
  },
  pt: {
    title: "Termos de Uso",
    updated: "Ultima atualizacao: 23 de outubro de 2026",
    metaDescription: "Termos de Uso do MoovaTV. Leia nossos termos e condicoes para uso do servico.",
    sections: [
      {
        title: "1. Aceitacao dos Termos",
        body: [
          "Ao acessar ou utilizar o MoovaTV (o \"Servico\"), incluindo nossos aplicativos para Smart TVs (Samsung, LG) e dispositivos de streaming (Roku, Amazon Fire TV), aplicativos moveis (Android e iOS) e o site moova.tv, voce concorda em ficar vinculado a estes Termos de Uso. Se voce nao concordar com qualquer parte destes termos, nao devera utilizar o Servico.",
          "Estes termos constituem um acordo legalmente vinculante entre voce (\"Usuario\") e MoovaTV (\"nos\", \"nosso\" ou \"Empresa\"). Recomendamos a leitura atenta antes de utilizar o Servico.",
        ],
      },
      {
        title: "2. Descricao do Servico",
        body: [
          "O MoovaTV e exclusivamente um aplicativo reprodutor de midia (media player). NOS NAO vendemos, distribuimos, hospedamos ou fornecemos playlists, canais, transmissoes ou conteudo de qualquer tipo. O Servico funciona unicamente como um reprodutor que permite aos usuarios organizar e reproduzir seu proprio conteudo de midia a partir de fontes externas.",
          "Os usuarios sao inteiramente responsaveis pelo conteudo que adicionam ao aplicativo, incluindo playlists, credenciais de playlist e quaisquer outras fontes de midia. O MoovaTV nao tem controle e nao assume responsabilidade pelo conteudo, politicas de privacidade ou praticas de fontes de terceiros.",
          "O Servico e fornecido \"como esta\" e \"conforme disponivel\", sem garantias de qualquer tipo, expressas ou implicitas.",
        ],
      },
      {
        title: "3. Conta e Ativacao",
        body: [
          "A ativacao de dispositivos no MoovaTV e feita atraves do endereco MAC unico do dispositivo. Cada licenca esta vinculada a um dispositivo especifico e nao e transferivel, salvo quando expressamente permitido pela nossa equipe de suporte.",
          "Oferecemos os seguintes planos: Teste Gratuito (7 dias), Plano Anual (R$ 30/ano) e Plano Vitalicio (R$ 60 pagamento unico). Os precos podem ser ajustados a qualquer momento, mas alteracoes nao afetarao planos ativos ja adquiridos.",
          "Os pagamentos sao processados de forma segura atraves do Gates2B / Stripe. Nao armazenamos informacoes completas de cartao de credito em nossos servidores. A criacao de contas tambem pode ocorrer atraves de revendedores autorizados.",
          "Voce e responsavel por manter a seguranca de suas credenciais de conta e por todas as atividades que ocorram em sua conta.",
        ],
      },
      {
        title: "4. Responsabilidades do Usuario",
        body: [
          "Voce e o unico responsavel por todo o conteudo que adiciona, acessa ou transmite atraves do Servico. Voce deve garantir que seu uso do MoovaTV esteja em conformidade com todas as leis e regulamentos locais, estaduais, nacionais e internacionais aplicaveis.",
          "Voce concorda em NAO utilizar o Servico para: acessar, distribuir ou transmitir conteudo que viole direitos de propriedade intelectual de terceiros; praticar qualquer atividade ilegal; tentar fazer engenharia reversa, descompilar ou desmontar o aplicativo; interferir ou interromper o Servico ou servidores; compartilhar suas credenciais de conta com terceiros nao autorizados; utilizar o Servico para redistribuicao comercial sem autorizacao.",
          "A violacao dessas responsabilidades pode resultar na rescisao imediata de sua conta sem reembolso.",
        ],
      },
      {
        title: "5. Propriedade Intelectual",
        body: [
          "A marca MoovaTV, logotipo, codigo-fonte, design da interface e todos os elementos visuais relacionados sao propriedade exclusiva da MoovaTV e estao protegidos pelas leis de propriedade intelectual aplicaveis.",
          "Voce recebe uma licenca limitada, nao exclusiva, intransferivel e revogavel para usar o Servico para fins pessoais e nao comerciais, de acordo com estes Termos.",
          "O conteudo adicionado pelos usuarios ao Servico permanece sob responsabilidade exclusiva dos respectivos usuarios. A MoovaTV nao reivindica propriedade sobre qualquer conteudo fornecido pelo usuario.",
        ],
      },
      {
        title: "6. Pagamento e Reembolso",
        body: [
          "Todos os pagamentos sao processados pelo Gates2B / Stripe utilizando criptografia e medidas de seguranca padrao da industria. Os metodos de pagamento aceitos incluem cartoes de credito e outros metodos disponiveis pelo Gates2B / Stripe.",
          "Oferecemos garantia de reembolso de 7 dias a partir da data da compra. Para solicitar um reembolso dentro deste periodo, entre em contato conosco em support@moova.tv com os detalhes da sua compra. Os reembolsos sao processados pelo metodo de pagamento original.",
          "Apos o periodo de 7 dias, os pagamentos nao sao reembolsaveis. O Plano Vitalicio fornece acesso durante toda a vida util do produto MoovaTV, ou seja, enquanto o Servico continuar em operacao. Nao garante disponibilidade perpetua do Servico.",
          "Precos promocionais e descontos estao sujeitos a condicoes especificas e podem ser revogados a qualquer momento.",
        ],
      },
      {
        title: "7. Limitacao de Responsabilidade",
        body: [
          "Na extensao maxima permitida pela lei aplicavel, a MoovaTV nao sera responsavel por quaisquer danos indiretos, incidentais, especiais, consequenciais ou punitivos decorrentes ou relacionados ao seu uso do Servico.",
          "Nao somos responsaveis por: qualquer conteudo acessado ou transmitido atraves do Servico pelos usuarios; a disponibilidade, precisao ou legalidade de conteudo ou servicos de terceiros; interrupcoes do servico, seja por manutencao, problemas tecnicos ou circunstancias fora de nosso controle; perda de dados resultante de mau funcionamento do dispositivo ou erro do usuario.",
          "Nossa responsabilidade total por quaisquer reclamacoes decorrentes do uso do Servico nao excedera o valor pago por voce pelo Servico nos 12 meses anteriores a reclamacao.",
        ],
      },
      {
        title: "8. Privacidade",
        body: [
          "Sua privacidade e importante para nos. A coleta, uso e protecao de suas informacoes pessoais e regida por nossa Politica de Privacidade, disponivel em moova.tv/privacy.",
          "Ao utilizar o Servico, voce consente com as praticas de dados descritas em nossa Politica de Privacidade. Recomendamos que voce a revise periodicamente para verificar atualizacoes.",
        ],
      },
      {
        title: "9. Modificacoes dos Termos",
        body: [
          "Reservamo-nos o direito de modificar estes Termos de Uso a qualquer momento. As alteracoes serao publicadas nesta pagina com uma data de revisao atualizada. Podemos notifica-lo sobre alteracoes significativas atraves do aplicativo ou por e-mail.",
          "O uso continuado do Servico apos quaisquer modificacoes constitui sua aceitacao dos termos atualizados. Se voce nao concordar com as alteracoes, devera descontinuar o uso do Servico.",
        ],
      },
      {
        title: "10. Rescisao",
        body: [
          "Reservamo-nos o direito de suspender ou encerrar sua conta e acesso ao Servico a qualquer momento, com ou sem aviso, por conduta que acreditemos violar estes Termos de Uso, seja prejudicial a outros usuarios ou de outra forma inaceitavel.",
          "Apos a rescisao, seu direito de usar o Servico cessa imediatamente. Disposicoes destes Termos que, por sua natureza, devam sobreviver a rescisao permanecerao em vigor, incluindo, entre outras, disposicoes de propriedade, isencoes de garantia e limitacoes de responsabilidade.",
          "Voce pode encerrar sua conta a qualquer momento entrando em contato conosco em support@moova.tv.",
        ],
      },
      {
        title: "11. Legislacao Aplicavel",
        body: [
          "Estes Termos de Uso serao regidos e interpretados de acordo com as leis da Republica Federativa do Brasil, sem considerar disposicoes sobre conflitos de leis.",
          "Quaisquer disputas decorrentes destes Termos ou do uso do Servico serao submetidas aos tribunais competentes do Brasil. Antes de iniciar procedimentos legais, as partes concordam em tentar resolver disputas amigavelmente.",
        ],
      },
      {
        title: "12. Contato",
        body: [
          "Se voce tiver duvidas, preocupacoes ou solicitacoes relacionadas a estes Termos de Uso, entre em contato conosco:",
        ],
      },
    ],
  },
  de: {
    title: "Nutzungsbedingungen",
    updated: "Letzte Aktualisierung: 23. Oktober 2026",
    metaDescription: "MoovaTV Nutzungsbedingungen. Lesen Sie unsere Bedingungen fur die Nutzung des Dienstes.",
    sections: [
      {
        title: "1. Annahme der Bedingungen",
        body: [
          "Durch den Zugriff auf oder die Nutzung von MoovaTV (der \"Dienst\"), einschliesslich unserer Anwendungen fur Smart TVs (Samsung, LG) und Streaming-Gerate (Roku, Amazon Fire TV), mobilen Apps (Android und iOS) und der Website moova.tv, erklaren Sie sich mit diesen Nutzungsbedingungen einverstanden. Wenn Sie mit einem Teil dieser Bedingungen nicht einverstanden sind, durfen Sie den Dienst nicht nutzen.",
          "Diese Bedingungen stellen eine rechtsverbindliche Vereinbarung zwischen Ihnen (\"Benutzer\") und MoovaTV (\"wir\", \"uns\" oder \"Unternehmen\") dar. Wir empfehlen, sie vor der Nutzung des Dienstes sorgfaltig zu lesen.",
        ],
      },
      {
        title: "2. Dienstbeschreibung",
        body: [
          "MoovaTV ist ausschliesslich eine Mediaplayer-Anwendung. Wir verkaufen, verteilen, hosten oder stellen KEINE Playlists, Kanale, Streams oder Inhalte jeglicher Art bereit. Der Dienst funktioniert ausschliesslich als Player, der es Benutzern ermoglicht, ihre eigenen Medieninhalte aus externen Quellen zu organisieren und abzuspielen.",
          "Die Benutzer sind vollstandig fur die Inhalte verantwortlich, die sie der Anwendung hinzufugen, einschliesslich Playlists, Playlist-Zugangsdaten und anderer Medienquellen. MoovaTV hat keine Kontrolle uber Inhalte, Datenschutzrichtlinien oder Praktiken von Drittanbieterquellen und ubernimmt keine Verantwortung dafur.",
          "Der Dienst wird \"wie besehen\" und \"wie verfugbar\" ohne jegliche ausdruckliche oder stillschweigende Garantien bereitgestellt.",
        ],
      },
      {
        title: "3. Konto und Aktivierung",
        body: [
          "Die Gerateaktivierung bei MoovaTV erfolgt uber die eindeutige MAC-Adresse des Gerats. Jede Lizenz ist an ein bestimmtes Gerat gebunden und nicht ubertragbar, es sei denn, dies wird ausdrucklich von unserem Support-Team genehmigt.",
          "Wir bieten folgende Plane an: Kostenlose Testversion (7 Tage), Jahresplan (6 €/Jahr) und Lebenslanger Plan (12 € einmalige Zahlung). Preise konnen jederzeit angepasst werden, Anderungen betreffen jedoch keine bereits erworbenen aktiven Plane.",
          "Zahlungen werden sicher uber Gates2B / Stripe abgewickelt. Wir speichern keine vollstandigen Kreditkarteninformationen auf unseren Servern. Die Kontoerstellung kann auch uber autorisierte Handler erfolgen.",
          "Sie sind fur die Sicherheit Ihrer Kontodaten und fur alle Aktivitaten verantwortlich, die unter Ihrem Konto stattfinden.",
        ],
      },
      {
        title: "4. Benutzerverantwortlichkeiten",
        body: [
          "Sie sind allein verantwortlich fur alle Inhalte, die Sie uber den Dienst hinzufugen, darauf zugreifen oder streamen. Sie mussen sicherstellen, dass Ihre Nutzung von MoovaTV allen geltenden lokalen, staatlichen, nationalen und internationalen Gesetzen und Vorschriften entspricht.",
          "Sie erklaren sich damit einverstanden, den Dienst NICHT zu nutzen, um: auf Inhalte zuzugreifen, diese zu verbreiten oder zu streamen, die geistige Eigentumsrechte Dritter verletzen; illegale Aktivitaten durchzufuhren; zu versuchen, die Anwendung zu reverse-engineeren, zu dekompilieren oder zu disassemblieren; den Dienst oder die Server zu storen oder zu beeintrachtigen; Ihre Kontodaten an unbefugte Dritte weiterzugeben; den Dienst fur kommerzielle Weiterverbreitung ohne Genehmigung zu nutzen.",
          "Ein Verstoss gegen diese Verantwortlichkeiten kann zur sofortigen Kundigung Ihres Kontos ohne Ruckerstattung fuhren.",
        ],
      },
      {
        title: "5. Geistiges Eigentum",
        body: [
          "Die Marke MoovaTV, das Logo, der Quellcode, das Design der Benutzeroberflache und alle zugehorigen visuellen Elemente sind ausschliessliches Eigentum von MoovaTV und durch geltende Gesetze zum Schutz geistigen Eigentums geschutzt.",
          "Ihnen wird eine beschrankte, nicht-exklusive, nicht ubertragbare, widerrufliche Lizenz zur Nutzung des Dienstes fur personliche, nicht-kommerzielle Zwecke gemaess diesen Bedingungen gewahrt.",
          "Von Benutzern zum Dienst hinzugefugte Inhalte verbleiben in der alleinigen Verantwortung der jeweiligen Benutzer. MoovaTV erhebt keinen Eigentumsanspruch auf von Benutzern bereitgestellte Inhalte.",
        ],
      },
      {
        title: "6. Zahlung und Ruckerstattung",
        body: [
          "Alle Zahlungen werden uber Gates2B / Stripe mit branchenueblicher Verschlusselung und Sicherheitsmassnahmen verarbeitet. Akzeptierte Zahlungsmethoden umfassen Kreditkarten und andere uber Gates2B / Stripe verfugbare Methoden.",
          "Wir bieten eine 7-tagige Geld-zuruck-Garantie ab Kaufdatum. Um innerhalb dieses Zeitraums eine Ruckerstattung zu beantragen, kontaktieren Sie uns unter support@moova.tv mit Ihren Kaufdetails. Ruckerstattungen werden uber die ursprungliche Zahlungsmethode abgewickelt.",
          "Nach Ablauf der 7-Tage-Frist sind Zahlungen nicht erstattungsfahig. Der Lebenslange Plan bietet Zugang fur die gesamte Lebensdauer des MoovaTV-Produkts, d.h. solange der Dienst in Betrieb ist. Er garantiert keine unbefristete Verfugbarkeit des Dienstes.",
          "Aktionspreise und Rabatte unterliegen besonderen Bedingungen und konnen jederzeit widerrufen werden.",
        ],
      },
      {
        title: "7. Haftungsbeschrankung",
        body: [
          "Im maximal gesetzlich zulassigen Umfang haftet MoovaTV nicht fur indirekte, zufallige, besondere, Folge- oder Strafschaden, die aus oder im Zusammenhang mit Ihrer Nutzung des Dienstes entstehen.",
          "Wir sind nicht verantwortlich fur: Inhalte, auf die Benutzer uber den Dienst zugreifen oder die sie streamen; die Verfugbarkeit, Richtigkeit oder Rechtmassigkeit von Inhalten oder Diensten Dritter; Dienstunterbrechungen, sei es durch Wartung, technische Probleme oder Umstande ausserhalb unserer Kontrolle; Datenverlust durch Geratefehlfunktionen oder Benutzerfehler.",
          "Unsere Gesamthaftung fur Anspruche aus der Nutzung des Dienstes ubersteigt nicht den Betrag, den Sie in den 12 Monaten vor dem Anspruch fur den Dienst gezahlt haben.",
        ],
      },
      {
        title: "8. Datenschutz",
        body: [
          "Ihr Datenschutz ist uns wichtig. Die Erhebung, Nutzung und der Schutz Ihrer personlichen Daten werden durch unsere Datenschutzrichtlinie geregelt, verfugbar unter moova.tv/privacy.",
          "Durch die Nutzung des Dienstes stimmen Sie den in unserer Datenschutzrichtlinie beschriebenen Datenpraktiken zu. Wir empfehlen Ihnen, diese regelmaessig auf Aktualisierungen zu uberprufen.",
        ],
      },
      {
        title: "9. Anderungen der Bedingungen",
        body: [
          "Wir behalten uns das Recht vor, diese Nutzungsbedingungen jederzeit zu andern. Anderungen werden auf dieser Seite mit einem aktualisierten Revisionsdatum veroffentlicht. Wir konnen Sie uber wesentliche Anderungen uber die Anwendung oder per E-Mail benachrichtigen.",
          "Ihre fortgesetzte Nutzung des Dienstes nach Anderungen stellt Ihre Annahme der aktualisierten Bedingungen dar. Wenn Sie mit den Anderungen nicht einverstanden sind, sollten Sie die Nutzung des Dienstes einstellen.",
        ],
      },
      {
        title: "10. Kundigung",
        body: [
          "Wir behalten uns das Recht vor, Ihr Konto und den Zugang zum Dienst jederzeit, mit oder ohne Vorankuendigung, zu sperren oder zu kundigen, bei Verhalten, das unserer Meinung nach gegen diese Nutzungsbedingungen verstosst, anderen Benutzern schadet oder anderweitig unangemessen ist.",
          "Nach der Kundigung erlischt Ihr Recht zur Nutzung des Dienstes sofort. Bestimmungen dieser Bedingungen, die ihrer Natur nach die Kundigung uberdauern sollten, bleiben in Kraft, einschliesslich, aber nicht beschrankt auf Eigentumsbestimmungen, Garantieausschlusse und Haftungsbeschrankungen.",
          "Sie konnen Ihr Konto jederzeit kundigen, indem Sie uns unter support@moova.tv kontaktieren.",
        ],
      },
      {
        title: "11. Anwendbares Recht",
        body: [
          "Diese Nutzungsbedingungen unterliegen dem Recht der Foderativen Republik Brasilien und werden danach ausgelegt, ohne Berucksichtigung von Kollisionsnormen.",
          "Alle Streitigkeiten aus diesen Bedingungen oder der Nutzung des Dienstes werden den zustandigen Gerichten Brasiliens vorgelegt. Vor Einleitung rechtlicher Schritte verpflichten sich die Parteien, Streitigkeiten gutlich beizulegen.",
        ],
      },
      {
        title: "12. Kontakt",
        body: [
          "Wenn Sie Fragen, Bedenken oder Anfragen zu diesen Nutzungsbedingungen haben, kontaktieren Sie uns bitte:",
        ],
      },
    ],
  },
  fr: {
    title: "Conditions d'Utilisation",
    updated: "Derniere mise a jour : 23 octobre 2026",
    metaDescription: "Conditions d'utilisation de MoovaTV. Lisez nos termes et conditions pour l'utilisation du service.",
    sections: [
      {
        title: "1. Acceptation des Conditions",
        body: [
          "En accedant ou en utilisant MoovaTV (le \"Service\"), y compris nos applications pour Smart TV (Samsung, LG) et appareils de streaming (Roku, Amazon Fire TV), applications mobiles (Android et iOS) et le site moova.tv, vous acceptez d'etre lie par ces Conditions d'Utilisation. Si vous n'acceptez pas une partie de ces conditions, vous ne devez pas utiliser le Service.",
          "Ces conditions constituent un accord juridiquement contraignant entre vous (\"Utilisateur\") et MoovaTV (\"nous\", \"notre\" ou \"Societe\"). Nous vous recommandons de les lire attentivement avant d'utiliser le Service.",
        ],
      },
      {
        title: "2. Description du Service",
        body: [
          "MoovaTV est exclusivement une application lecteur multimedia. Nous NE vendons PAS, ne distribuons pas, n'hebergeons pas et ne fournissons pas de playlists, de chaines, de flux ou de contenu de quelque nature que ce soit. Le Service fonctionne uniquement comme un lecteur permettant aux utilisateurs d'organiser et de lire leur propre contenu multimedia a partir de sources externes.",
          "Les utilisateurs sont entierement responsables du contenu qu'ils ajoutent a l'application, y compris les playlists, les identifiants de playlist et toute autre source multimedia. MoovaTV n'a aucun controle et n'assume aucune responsabilite quant au contenu, aux politiques de confidentialite ou aux pratiques des sources tierces.",
          "Le Service est fourni \"tel quel\" et \"selon disponibilite\" sans garantie d'aucune sorte, expresse ou implicite.",
        ],
      },
      {
        title: "3. Compte et Activation",
        body: [
          "L'activation des appareils sur MoovaTV se fait via l'adresse MAC unique de l'appareil. Chaque licence est liee a un appareil specifique et n'est pas transferable sauf autorisation expresse de notre equipe d'assistance.",
          "Nous proposons les forfaits suivants : Essai Gratuit (7 jours), Forfait Annuel (6 €/an) et Forfait a Vie (12 € paiement unique). Les prix peuvent etre ajustes a tout moment, mais les modifications n'affecteront pas les forfaits actifs deja achetes.",
          "Les paiements sont traites de maniere securisee via Gates2B / Stripe. Nous ne stockons pas les informations completes de carte de credit sur nos serveurs. La creation de compte peut egalement se faire via des revendeurs autorises.",
          "Vous etes responsable de la securite de vos identifiants de compte et de toutes les activites qui se produisent sous votre compte.",
        ],
      },
      {
        title: "4. Responsabilites de l'Utilisateur",
        body: [
          "Vous etes seul responsable de tout contenu que vous ajoutez, accedez ou diffusez via le Service. Vous devez vous assurer que votre utilisation de MoovaTV est conforme a toutes les lois et reglementations locales, etatiques, nationales et internationales applicables.",
          "Vous acceptez de NE PAS utiliser le Service pour : acceder, distribuer ou diffuser du contenu portant atteinte aux droits de propriete intellectuelle de tiers ; exercer une activite illegale ; tenter de faire de l'ingenierie inverse, decompiler ou desassembler l'application ; interferer avec ou perturber le Service ou les serveurs ; partager vos identifiants de compte avec des tiers non autorises ; utiliser le Service pour une redistribution commerciale sans autorisation.",
          "La violation de ces responsabilites peut entrainer la resiliation immediate de votre compte sans remboursement.",
        ],
      },
      {
        title: "5. Propriete Intellectuelle",
        body: [
          "La marque MoovaTV, le logo, le code source, le design de l'interface utilisateur et tous les elements visuels associes sont la propriete exclusive de MoovaTV et sont proteges par les lois applicables sur la propriete intellectuelle.",
          "Vous beneficiez d'une licence limitee, non exclusive, non transferable et revocable pour utiliser le Service a des fins personnelles et non commerciales conformement a ces Conditions.",
          "Le contenu ajoute par les utilisateurs au Service reste sous la responsabilite exclusive des utilisateurs respectifs. MoovaTV ne revendique aucun droit de propriete sur le contenu fourni par les utilisateurs.",
        ],
      },
      {
        title: "6. Paiement et Remboursement",
        body: [
          "Tous les paiements sont traites via Gates2B / Stripe en utilisant un chiffrement et des mesures de securite conformes aux normes de l'industrie. Les methodes de paiement acceptees incluent les cartes de credit et autres methodes disponibles via Gates2B / Stripe.",
          "Nous offrons une garantie de remboursement de 7 jours a compter de la date d'achat. Pour demander un remboursement dans ce delai, contactez-nous a support@moova.tv avec les details de votre achat. Les remboursements sont traites via le mode de paiement d'origine.",
          "Passe le delai de 7 jours, les paiements ne sont pas remboursables. Le Forfait a Vie donne acces pendant toute la duree de vie du produit MoovaTV, c'est-a-dire tant que le Service continue de fonctionner. Il ne garantit pas la disponibilite perpetuelle du Service.",
          "Les prix promotionnels et remises sont soumis a des conditions specifiques et peuvent etre revoques a tout moment.",
        ],
      },
      {
        title: "7. Limitation de Responsabilite",
        body: [
          "Dans la mesure maximale permise par la loi applicable, MoovaTV ne sera pas responsable des dommages indirects, accessoires, speciaux, consecutifs ou punitifs decoulant de ou lies a votre utilisation du Service.",
          "Nous ne sommes pas responsables : du contenu accede ou diffuse via le Service par les utilisateurs ; de la disponibilite, de l'exactitude ou de la legalite du contenu ou des services tiers ; des interruptions de service, qu'elles soient dues a la maintenance, des problemes techniques ou des circonstances independantes de notre volonte ; de la perte de donnees resultant d'un dysfonctionnement de l'appareil ou d'une erreur de l'utilisateur.",
          "Notre responsabilite totale pour toute reclamation decoulant de l'utilisation du Service ne depassera pas le montant que vous avez paye pour le Service au cours des 12 mois precedant la reclamation.",
        ],
      },
      {
        title: "8. Confidentialite",
        body: [
          "Votre vie privee est importante pour nous. La collecte, l'utilisation et la protection de vos informations personnelles sont regies par notre Politique de Confidentialite, disponible sur moova.tv/privacy.",
          "En utilisant le Service, vous consentez aux pratiques de donnees decrites dans notre Politique de Confidentialite. Nous vous encourageons a la consulter regulierement pour toute mise a jour.",
        ],
      },
      {
        title: "9. Modifications des Conditions",
        body: [
          "Nous nous reservons le droit de modifier ces Conditions d'Utilisation a tout moment. Les modifications seront publiees sur cette page avec une date de revision mise a jour. Nous pouvons vous informer des modifications importantes via l'application ou par e-mail.",
          "Votre utilisation continue du Service apres toute modification constitue votre acceptation des conditions mises a jour. Si vous n'acceptez pas les modifications, vous devez cesser d'utiliser le Service.",
        ],
      },
      {
        title: "10. Resiliation",
        body: [
          "Nous nous reservons le droit de suspendre ou de resilier votre compte et l'acces au Service a tout moment, avec ou sans preavis, pour un comportement que nous estimons contraire a ces Conditions d'Utilisation, prejudiciable aux autres utilisateurs ou autrement reprehensible.",
          "A la resiliation, votre droit d'utiliser le Service cesse immediatement. Les dispositions de ces Conditions qui, par leur nature, doivent survivre a la resiliation restent en vigueur, y compris, sans s'y limiter, les dispositions relatives a la propriete, les exclusions de garantie et les limitations de responsabilite.",
          "Vous pouvez resilier votre compte a tout moment en nous contactant a support@moova.tv.",
        ],
      },
      {
        title: "11. Droit Applicable",
        body: [
          "Ces Conditions d'Utilisation sont regies et interpretees conformement aux lois de la Republique Federative du Bresil, sans tenir compte des dispositions relatives aux conflits de lois.",
          "Tout litige decoulant de ces Conditions ou de l'utilisation du Service sera soumis aux tribunaux competents du Bresil. Avant d'engager une procedure judiciaire, les parties conviennent de tenter de resoudre les litiges a l'amiable.",
        ],
      },
      {
        title: "12. Contact",
        body: [
          "Si vous avez des questions, des preoccupations ou des demandes concernant ces Conditions d'Utilisation, veuillez nous contacter :",
        ],
      },
    ],
  },
  es: {
    title: "Terminos de Uso",
    updated: "Ultima actualizacion: 23 de octubre de 2026",
    metaDescription: "Terminos de Uso de MoovaTV. Lea nuestros terminos y condiciones para el uso del servicio.",
    sections: [
      {
        title: "1. Aceptacion de los Terminos",
        body: [
          "Al acceder o utilizar MoovaTV (el \"Servicio\"), incluyendo nuestras aplicaciones para Smart TVs (Samsung, LG) y dispositivos de streaming (Roku, Amazon Fire TV), aplicaciones moviles (Android e iOS) y el sitio web moova.tv, usted acepta quedar vinculado por estos Terminos de Uso. Si no esta de acuerdo con alguna parte de estos terminos, no debera utilizar el Servicio.",
          "Estos terminos constituyen un acuerdo legalmente vinculante entre usted (\"Usuario\") y MoovaTV (\"nosotros\", \"nuestro\" o \"Empresa\"). Recomendamos leerlos cuidadosamente antes de utilizar el Servicio.",
        ],
      },
      {
        title: "2. Descripcion del Servicio",
        body: [
          "MoovaTV es exclusivamente una aplicacion reproductora de medios (media player). NO vendemos, distribuimos, alojamos ni proporcionamos listas de reproduccion, canales, transmisiones ni contenido de ningun tipo. El Servicio funciona unicamente como un reproductor que permite a los usuarios organizar y reproducir su propio contenido multimedia desde fuentes externas.",
          "Los usuarios son completamente responsables del contenido que agregan a la aplicacion, incluyendo listas de reproduccion, credenciales de listas y cualquier otra fuente de medios. MoovaTV no tiene control y no asume responsabilidad por el contenido, politicas de privacidad o practicas de fuentes de terceros.",
          "El Servicio se proporciona \"tal cual\" y \"segun disponibilidad\" sin garantias de ningun tipo, ya sean expresas o implicitas.",
        ],
      },
      {
        title: "3. Cuenta y Activacion",
        body: [
          "La activacion de dispositivos en MoovaTV se realiza a traves de la direccion MAC unica del dispositivo. Cada licencia esta vinculada a un dispositivo especifico y no es transferible a menos que nuestro equipo de soporte lo autorice expresamente.",
          "Ofrecemos los siguientes planes: Prueba Gratuita (7 dias), Plan Anual (6 €/anio) y Plan Vitalicio (12 € pago unico). Los precios pueden ajustarse en cualquier momento, pero los cambios no afectaran los planes activos ya adquiridos.",
          "Los pagos se procesan de forma segura a traves de Gates2B / Stripe. No almacenamos informacion completa de tarjetas de credito en nuestros servidores. La creacion de cuentas tambien puede realizarse a traves de revendedores autorizados.",
          "Usted es responsable de mantener la seguridad de sus credenciales de cuenta y de todas las actividades que ocurran bajo su cuenta.",
        ],
      },
      {
        title: "4. Responsabilidades del Usuario",
        body: [
          "Usted es el unico responsable de todo el contenido que agrega, accede o transmite a traves del Servicio. Debe asegurarse de que su uso de MoovaTV cumpla con todas las leyes y regulaciones locales, estatales, nacionales e internacionales aplicables.",
          "Usted acepta NO utilizar el Servicio para: acceder, distribuir o transmitir contenido que infrinja derechos de propiedad intelectual de terceros; participar en actividades ilegales; intentar realizar ingenieria inversa, descompilar o desensamblar la aplicacion; interferir con o interrumpir el Servicio o los servidores; compartir sus credenciales de cuenta con terceros no autorizados; utilizar el Servicio para redistribucion comercial sin autorizacion.",
          "La violacion de estas responsabilidades puede resultar en la terminacion inmediata de su cuenta sin reembolso.",
        ],
      },
      {
        title: "5. Propiedad Intelectual",
        body: [
          "La marca MoovaTV, el logotipo, el codigo fuente, el diseno de la interfaz de usuario y todos los elementos visuales relacionados son propiedad exclusiva de MoovaTV y estan protegidos por las leyes de propiedad intelectual aplicables.",
          "Se le otorga una licencia limitada, no exclusiva, intransferible y revocable para utilizar el Servicio con fines personales y no comerciales de acuerdo con estos Terminos.",
          "El contenido agregado por los usuarios al Servicio permanece bajo la responsabilidad exclusiva de los respectivos usuarios. MoovaTV no reclama propiedad sobre ningun contenido proporcionado por los usuarios.",
        ],
      },
      {
        title: "6. Pago y Reembolso",
        body: [
          "Todos los pagos se procesan a traves de Gates2B / Stripe utilizando cifrado y medidas de seguridad estandar de la industria. Los metodos de pago aceptados incluyen tarjetas de credito y otros metodos disponibles a traves de Gates2B / Stripe.",
          "Ofrecemos una garantia de devolucion de dinero de 7 dias a partir de la fecha de compra. Para solicitar un reembolso dentro de este periodo, contactenos en support@moova.tv con los detalles de su compra. Los reembolsos se procesan a traves del metodo de pago original.",
          "Despues del periodo de 7 dias, los pagos no son reembolsables. El Plan Vitalicio proporciona acceso durante toda la vida util del producto MoovaTV, es decir, mientras el Servicio continue en operacion. No garantiza la disponibilidad perpetua del Servicio.",
          "Los precios promocionales y descuentos estan sujetos a condiciones especificas y pueden ser revocados en cualquier momento.",
        ],
      },
      {
        title: "7. Limitacion de Responsabilidad",
        body: [
          "En la medida maxima permitida por la ley aplicable, MoovaTV no sera responsable de ningun dano indirecto, incidental, especial, consecuente o punitivo que surja de o este relacionado con su uso del Servicio.",
          "No somos responsables de: cualquier contenido al que los usuarios accedan o transmitan a traves del Servicio; la disponibilidad, precision o legalidad de contenido o servicios de terceros; interrupciones del servicio, ya sea por mantenimiento, problemas tecnicos o circunstancias fuera de nuestro control; perdida de datos resultante de mal funcionamiento del dispositivo o error del usuario.",
          "Nuestra responsabilidad total por cualquier reclamacion derivada del uso del Servicio no excedera el monto pagado por usted por el Servicio en los 12 meses anteriores a la reclamacion.",
        ],
      },
      {
        title: "8. Privacidad",
        body: [
          "Su privacidad es importante para nosotros. La recopilacion, uso y proteccion de su informacion personal se rige por nuestra Politica de Privacidad, disponible en moova.tv/privacy.",
          "Al utilizar el Servicio, usted consiente las practicas de datos descritas en nuestra Politica de Privacidad. Le recomendamos revisarla periodicamente para verificar actualizaciones.",
        ],
      },
      {
        title: "9. Modificaciones de los Terminos",
        body: [
          "Nos reservamos el derecho de modificar estos Terminos de Uso en cualquier momento. Los cambios se publicaran en esta pagina con una fecha de revision actualizada. Podemos notificarle sobre cambios significativos a traves de la aplicacion o por correo electronico.",
          "Su uso continuado del Servicio despues de cualquier modificacion constituye su aceptacion de los terminos actualizados. Si no esta de acuerdo con los cambios, debera dejar de utilizar el Servicio.",
        ],
      },
      {
        title: "10. Terminacion",
        body: [
          "Nos reservamos el derecho de suspender o cancelar su cuenta y acceso al Servicio en cualquier momento, con o sin previo aviso, por conducta que consideremos que viola estos Terminos de Uso, es perjudicial para otros usuarios o es de otra manera inaceptable.",
          "Tras la terminacion, su derecho a utilizar el Servicio cesa inmediatamente. Las disposiciones de estos Terminos que por su naturaleza deban sobrevivir a la terminacion permaneceran en vigor, incluyendo, entre otras, las disposiciones de propiedad, renuncias de garantia y limitaciones de responsabilidad.",
          "Puede cancelar su cuenta en cualquier momento contactandonos en support@moova.tv.",
        ],
      },
      {
        title: "11. Ley Aplicable",
        body: [
          "Estos Terminos de Uso se regiran e interpretaran de acuerdo con las leyes de la Republica Federativa de Brasil, sin tener en cuenta las disposiciones sobre conflictos de leyes.",
          "Cualquier disputa que surja de estos Terminos o del uso del Servicio se sometera a los tribunales competentes de Brasil. Antes de iniciar procedimientos legales, las partes acuerdan intentar resolver las disputas de manera amistosa.",
        ],
      },
      {
        title: "12. Contacto",
        body: [
          "Si tiene preguntas, inquietudes o solicitudes relacionadas con estos Terminos de Uso, contactenos:",
        ],
      },
    ],
  },
};

const TermsPage = () => {
  const { language } = useLanguage();
  const lang = (language || "en") as Lang;
  const urlLang =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("lang")
      : null;
  // Italian is reachable only via ?lang=it (Fire TV / Roku store listings)
  const c = urlLang === "it" ? termsIt : content[lang] || content.en;

  return (
    <>
      <Helmet>
        <title>{c.title} - MoovaTV</title>
        <meta name="description" content={c.metaDescription} />
      </Helmet>

      <div className="w-full py-16 md:py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 text-center text-white">
            {c.title}
          </h1>
          <p className="text-sm text-white/30 text-center mb-12">
            {c.updated}
          </p>

          <div className="space-y-8">
            {c.sections.map((section, i) => (
              <section key={i}>
                <h2 className="text-xl font-semibold text-white mb-3">
                  {section.title}
                </h2>
                {section.body.map((paragraph, j) => (
                  <p
                    key={j}
                    className="text-white/40 leading-relaxed mb-3 last:mb-0"
                  >
                    {paragraph}
                  </p>
                ))}
                {i === c.sections.length - 1 && (
                  <ul className="list-none pl-0 text-white/40 space-y-1 mt-3">
                    <li>
                      <strong className="text-white">E-mail:</strong>{" "}
                      <a
                        href="mailto:support@moova.tv"
                        className="text-white/60 hover:underline"
                      >
                        support@moova.tv
                      </a>
                    </li>
                    <li>
                      <strong className="text-white">Site:</strong>{" "}
                      <a
                        href="https://moova.tv"
                        className="text-white/60 hover:underline"
                      >
                        moova.tv
                      </a>
                    </li>
                  </ul>
                )}
              </section>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsPage;
